import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Crown, MessageSquare, AlertTriangle, ShoppingCart, CreditCard, ChevronRight, Package, Zap, History, X, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getMySubscription, getSubscriptionPlans, getSmsPackages, purchaseSubscription, purchaseSmsPackage, getPurchaseHistory, createPaytrToken, getOrderStatus } from '../services/api';
import { toast } from 'sonner';

const SubscriptionTab = ({ paymentResult: externalPaymentResult, onPaymentResultClear }) => {
  const [activeSubTab, setActiveSubTab] = useState('subscription');
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [smsPackages, setSmsPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseModal, setPurchaseModal] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // PayTR states
  const [paymentStep, setPaymentStep] = useState('confirm'); // confirm | iframe | polling | result
  const [iframeUrl, setIframeUrl] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const pollingRef = useRef(null);

  const [showPaymentBanner, setShowPaymentBanner] = useState(null);

  useEffect(() => {
    loadData();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  useEffect(() => {
    if (externalPaymentResult) {
      setShowPaymentBanner(externalPaymentResult);
      loadData();
      if (onPaymentResultClear) onPaymentResultClear();
    }
  }, [externalPaymentResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, pkgRes] = await Promise.all([
        getMySubscription(),
        getSubscriptionPlans(),
        getSmsPackages()
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
      setSmsPackages(pkgRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await getPurchaseHistory();
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePurchase = (item, type) => {
    setPurchaseModal({ item, type });
    setPaymentStep('confirm');
    setIframeUrl(null);
    setCurrentOrderId(null);
    setPaymentResult(null);
  };

  const startPayment = async () => {
    if (!purchaseModal) return;
    setProcessing(true);
    try {
      const { item, type } = purchaseModal;
      let orderRes;
      if (type === 'subscription') {
        orderRes = await purchaseSubscription(item.id);
      } else {
        orderRes = await purchaseSmsPackage(item.id);
      }
      const orderId = orderRes.data.id;
      setCurrentOrderId(orderId);

      // Get PayTR token
      const tokenRes = await createPaytrToken(orderId);
      if (tokenRes.data.status === 'success') {
        setIframeUrl(tokenRes.data.iframe_url);
        setPaymentStep('iframe');
        startPolling(orderId);
      } else {
        toast.error(tokenRes.data.message || 'PayTR token alınamadı');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('PayTR ayarları')) {
        toast.error('Ödeme sistemi henüz yapılandırılmamış. Lütfen yöneticinize başvurun.');
      } else {
        toast.error(detail || 'Bir hata oluştu');
      }
    } finally {
      setProcessing(false);
    }
  };

  const pollCountRef = useRef(0);

  const startPolling = useCallback((orderId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollCountRef.current = 0;
    pollingRef.current = setInterval(async () => {
      try {
        pollCountRef.current += 1;
        const res = await getOrderStatus(orderId);
        if (res.data.status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPaymentStep('result');
          setPaymentResult('success');
          loadData();
        } else if (res.data.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPaymentStep('result');
          setPaymentResult('failed');
        } else if (pollCountRef.current >= 20) {
          // 60 saniye sonra hâlâ pending — polling'i durdur
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch (e) {
        // Ignore polling errors
      }
    }, 3000);
  }, []);

  const closePaymentModal = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPurchaseModal(null);
    setPaymentStep('confirm');
    setIframeUrl(null);
    setCurrentOrderId(null);
    setPaymentResult(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(price);
  };

  const priceExVat = (price) => price / 1.20;

  const getMonthlyEquivalent = (plan) => {
    const price = plan.discounted_price > 0 ? plan.discounted_price : plan.price;
    if (plan.type === 'yearly') return price / 12;
    return price;
  };

  const getSavingsPercent = () => {
    const monthlyPlan = plans.find(p => p.type === 'monthly');
    const yearlyPlan = plans.find(p => p.type === 'yearly');
    if (!monthlyPlan || !yearlyPlan) return 0;
    const monthlyPrice = monthlyPlan.discounted_price > 0 ? monthlyPlan.discounted_price : monthlyPlan.price;
    const yearlyMonthly = getMonthlyEquivalent(yearlyPlan);
    if (monthlyPrice === 0) return 0;
    return Math.round(((monthlyPrice - yearlyMonthly) / monthlyPrice) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      {/* Payment Result Banner */}
      {showPaymentBanner && (
        <div className={`rounded-2xl p-4 flex items-center gap-3 ${showPaymentBanner === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="payment-result-banner">
          {showPaymentBanner === 'success' ? (
            <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
          ) : (
            <XCircle size={24} className="text-red-600 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-semibold ${showPaymentBanner === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {showPaymentBanner === 'success' ? 'Ödeme alındı!' : 'Ödeme başarısız'}
            </p>
            <p className={`text-xs ${showPaymentBanner === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {showPaymentBanner === 'success' ? 'Abonelik süreniz başarıyla uzatıldı.' : 'Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin.'}
            </p>
          </div>
          <button onClick={() => setShowPaymentBanner(null)} className="p-1 rounded-full hover:bg-black/5" data-testid="dismiss-payment-banner">
            <X size={16} className={showPaymentBanner === 'success' ? 'text-green-500' : 'text-red-500'} />
          </button>
        </div>
      )}

      {/* Current Status Card */}
      {subscription && (
        <div className={`ios-card border-l-4 ${subscription.locked ? 'border-l-red-500 bg-red-50/50' : subscription.grace_period ? 'border-l-orange-500 bg-orange-50/50' : subscription.is_active ? 'border-l-green-500' : 'border-l-red-500 bg-red-50/50'}`} data-testid="subscription-status-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Crown size={18} className={subscription.is_active ? 'text-green-600' : 'text-red-500'} />
              <span className="text-sm font-semibold text-gray-900">Abonelik Durumu</span>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${subscription.locked ? 'bg-red-100 text-red-600' : subscription.grace_period ? 'bg-orange-100 text-orange-600' : subscription.is_active && subscription.is_trial ? 'bg-blue-100 text-blue-600' : subscription.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              {subscription.locked ? 'Kilitli' : subscription.grace_period ? 'Ek Süre' : subscription.is_active && subscription.is_trial ? 'Deneme Sürümü' : subscription.is_active ? 'Aktif' : 'Pasif'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#8E8E93]">
                {subscription.subscription_end ? `Bitiş: ${new Date(subscription.subscription_end).toLocaleDateString('tr-TR')}` : 'Tanımsız'}
              </p>
              {subscription.days_remaining !== null && subscription.days_remaining !== undefined && (
                <p className={`text-xs font-medium mt-0.5 ${subscription.days_remaining <= 3 ? 'text-red-500' : subscription.days_remaining <= 7 ? 'text-orange-500' : 'text-gray-600'}`}>
                  {subscription.grace_period
                    ? `Panelinizin kapanmasına son ${subscription.days_remaining} gün`
                    : `${subscription.days_remaining} gün kaldı`}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-[#8E8E93]">SMS Kontör</p>
              <p className="text-lg font-bold text-gray-900">{subscription.sms_credits || 0}</p>
            </div>
          </div>
          {subscription.grace_period && (
            <div className="mt-3 p-2.5 bg-orange-100 rounded-xl flex items-start gap-2">
              <AlertTriangle size={16} className="text-orange-600 mt-0.5 shrink-0" />
              <p className="text-xs text-orange-700">Aboneliğiniz sona erdi. {subscription.days_remaining} gün içinde yenileyin, aksi halde paneliniz kilitlenecektir.</p>
            </div>
          )}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex bg-[#F2F2F7] rounded-xl p-1 gap-1">
        <button
          onClick={() => setActiveSubTab('subscription')}
          className={`flex-1 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeSubTab === 'subscription' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
          data-testid="subtab-subscription"
        >
          <Crown size={15} />
          Abonelik
        </button>
        <button
          onClick={() => setActiveSubTab('sms')}
          className={`flex-1 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeSubTab === 'sms' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
          data-testid="subtab-sms"
        >
          <MessageSquare size={15} />
          SMS Paketi
        </button>
      </div>

      {/* Subscription Plans */}
      {activeSubTab === 'subscription' && (
        <div className="space-y-3">
          {plans.length > 0 ? (
            <>
              {getSavingsPercent() > 0 && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <Zap size={14} className="text-[#FF9500]" />
                  <span className="text-xs font-semibold text-[#FF9500]">Yıllık planlarda %{getSavingsPercent()} tasarruf!</span>
                </div>
              )}
              {plans.map((plan) => {
                const isYearly = plan.type === 'yearly';
                const hasDiscount = plan.discounted_price > 0 && plan.discounted_price < plan.price;
                const features = [
                  'Sınırsız müşteri & hasta kaydı',
                  'Randevu yönetimi & online portal',
                  'Stok takibi & barkod sistemi',
                  'Dijital hasta dosyası & lab sonuçları',
                  'SMS bildirim & hatırlatmalar',
                  'Konaklama (boarding) modülü',
                  'Detaylı raporlama & istatistikler',
                  'Ekip yönetimi & yetkilendirme',
                ];
                return (
                  <div key={plan.id} className={`ios-card relative overflow-hidden ${isYearly ? 'ring-2 ring-[#6b6de3]' : ''}`} data-testid={`plan-card-${plan.type}`}>
                    {isYearly && getSavingsPercent() > 0 && (
                      <div className="absolute top-0 right-0 bg-[#6b6de3] text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                        %{getSavingsPercent()} TASARRUF
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900">{plan.name}</h3>
                        <p className="text-xs text-[#8E8E93]">{isYearly ? '365 gün erişim' : '30 gün erişim'}</p>
                      </div>
                      <div className="text-right">
                        {hasDiscount && (
                          <p className="text-sm text-[#8E8E93] line-through">{formatPrice(priceExVat(plan.price))}</p>
                        )}
                        {isYearly ? (
                          <>
                            <p className="text-xl font-bold text-gray-900">
                              {formatPrice(priceExVat(getMonthlyEquivalent(plan)))}
                              <span className="text-[10px] font-normal text-[#8E8E93] ml-0.5">+KDV/ay</span>
                            </p>
                            <p className="text-xs text-[#8E8E93]">yıllık {formatPrice(priceExVat(hasDiscount ? plan.discounted_price : plan.price))}+KDV</p>
                          </>
                        ) : (
                          <p className="text-xl font-bold text-gray-900">
                            {formatPrice(priceExVat(hasDiscount ? plan.discounted_price : plan.price))}
                            <span className="text-[10px] font-normal text-[#8E8E93] ml-0.5">+KDV</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4">
                      {features.map((f, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <CheckCircle size={13} className="text-[#6b6de3] flex-shrink-0" />
                          <span className="text-[11px] text-gray-600 leading-tight">{f}</span>
                        </div>
                      ))}
                    </div>
                    {isYearly && (
                      <div className="mb-3 p-2.5 bg-gradient-to-r from-[#6b6de3]/10 to-[#8b5cf6]/10 rounded-xl border border-[#6b6de3]/20">
                        <p className="text-xs font-bold text-[#6b6de3] text-center">Mesleğe yeni başlayanlara +1 yıl hediye!</p>
                        <p className="text-[11px] text-[#6b6de3]/70 text-center mt-0.5">1 yıl öde, toplam 2 yıl kullan</p>
                      </div>
                    )}
                    <Button
                      onClick={() => handlePurchase(plan, 'subscription')}
                      className={`w-full rounded-full h-11 font-semibold ${isYearly ? 'bg-[#6b6de3] hover:bg-[#5658c4]' : 'bg-gray-900 hover:bg-gray-800'}`}
                      data-testid={`buy-plan-${plan.type}`}
                    >
                      <ShoppingCart size={16} className="mr-2" />
                      Satın Al
                    </Button>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="ios-card text-center py-8">
              <Crown size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">Henüz abonelik planı tanımlanmamış</p>
            </div>
          )}
        </div>
      )}

      {/* SMS Packages */}
      {activeSubTab === 'sms' && (
        <div className="space-y-3">
          {smsPackages.length > 0 ? (
            smsPackages.map((pkg) => {
              const hasDiscount = pkg.discounted_price > 0 && pkg.discounted_price < pkg.price;
              const pricePerSms = (hasDiscount ? pkg.discounted_price : pkg.price) / (pkg.sms_count || 1);
              const isBestSeller = pkg.sms_count >= 5000;
              return (
                <div key={pkg.id} className={`ios-card relative overflow-hidden ${isBestSeller ? 'ring-2 ring-[#34C759]' : ''}`} data-testid={`sms-package-${pkg.id}`}>
                  {isBestSeller && (
                    <div className="absolute top-0 right-0 bg-[#34C759] text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                      EN COK SATAN
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-900">{pkg.name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Package size={14} className="text-[#6b6de3]" />
                        <span className="text-sm font-semibold text-[#6b6de3]">{pkg.sms_count} SMS</span>
                      </div>
                      <p className="text-[10px] text-[#8E8E93] mt-0.5">{formatPrice(priceExVat(pricePerSms))} / SMS</p>
                    </div>
                    <div className="text-right">
                      {hasDiscount && (
                        <p className="text-sm text-[#8E8E93] line-through">{formatPrice(priceExVat(pkg.price))}</p>
                      )}
                      <p className="text-xl font-bold text-gray-900">
                        {formatPrice(priceExVat(hasDiscount ? pkg.discounted_price : pkg.price))}
                        <span className="text-[10px] font-normal text-[#8E8E93] ml-0.5">+KDV</span>
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handlePurchase(pkg, 'sms')}
                    className="w-full rounded-full h-11 font-semibold bg-[#34C759] hover:bg-green-600"
                    data-testid={`buy-sms-${pkg.id}`}
                  >
                    <ShoppingCart size={16} className="mr-2" />
                    Satın Al
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="ios-card text-center py-8">
              <MessageSquare size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">Henüz SMS paketi tanımlanmamış</p>
            </div>
          )}
        </div>
      )}

      {/* Purchase History Button */}
      <button
        onClick={() => { setShowHistory(true); loadHistory(); }}
        className="w-full ios-card flex items-center justify-between"
        data-testid="purchase-history-btn"
      >
        <div className="flex items-center gap-2">
          <History size={18} className="text-[#8E8E93]" />
          <span className="text-sm font-medium text-gray-900">Satın Alma Geçmişi</span>
        </div>
        <ChevronRight size={18} className="text-[#C7C7CC]" />
      </button>

      {/* Purchase / Payment Modal */}
      <Dialog open={!!purchaseModal} onOpenChange={(open) => { if (!open) closePaymentModal(); }}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-center">
              {paymentStep === 'iframe' ? 'Ödeme' : paymentStep === 'result' ? (paymentResult === 'success' ? 'Ödeme Başarılı' : 'Ödeme Başarısız') : purchaseModal?.type === 'subscription' ? 'Abonelik Satın Al' : 'SMS Paketi Satın Al'}
            </DialogTitle>
          </DialogHeader>
          {purchaseModal && (
            <div className="space-y-4 mt-2">
              {/* Step: Confirm */}
              {paymentStep === 'confirm' && (
                <>
                  <div className="bg-[#F2F2F7] rounded-2xl p-4 text-center">
                    <p className="font-bold text-lg text-gray-900">{purchaseModal.item.name}</p>
                    {purchaseModal.type === 'subscription' ? (
                      <p className="text-sm text-[#8E8E93]">{purchaseModal.item.type === 'yearly' ? '365 gün' : '30 gün'} süre uzatma</p>
                    ) : (
                      <p className="text-sm text-[#8E8E93]">{purchaseModal.item.sms_count} SMS kontör</p>
                    )}
                    <p className="text-2xl font-bold text-[#6b6de3] mt-2">
                      {formatPrice(
                        purchaseModal.item.discounted_price > 0 && purchaseModal.item.discounted_price < purchaseModal.item.price
                          ? purchaseModal.item.discounted_price
                          : purchaseModal.item.price
                      )}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="ghost"
                      onClick={closePaymentModal}
                      className="flex-1 rounded-full"
                      disabled={processing}
                    >
                      Vazgeç
                    </Button>
                    <Button
                      onClick={startPayment}
                      disabled={processing}
                      className="flex-1 rounded-full bg-[#6b6de3] hover:bg-[#5658c4] font-semibold"
                      data-testid="confirm-purchase-btn"
                    >
                      {processing ? (
                        <><Loader2 size={16} className="mr-2 animate-spin" />İşleniyor...</>
                      ) : (
                        <><CreditCard size={16} className="mr-2" />Ödemeye Geç</>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* Step: PayTR iFrame */}
              {paymentStep === 'iframe' && iframeUrl && (
                <div className="space-y-3">
                  <div className="bg-[#F2F2F7] rounded-2xl overflow-hidden" style={{minHeight: '400px'}}>
                    <iframe
                      src={iframeUrl}
                      id="paytriframe"
                      frameBorder="0"
                      scrolling="yes"
                      style={{width: '100%', minHeight: '400px', border: 'none'}}
                      title="PayTR Ödeme"
                      data-testid="paytr-iframe"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs text-[#8E8E93]">
                    <Loader2 size={12} className="animate-spin" />
                    <span>Ödeme bekleniyor...</span>
                  </div>
                </div>
              )}

              {/* Step: Result */}
              {paymentStep === 'result' && (
                <div className="text-center space-y-4">
                  {paymentResult === 'success' ? (
                    <>
                      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle size={32} className="text-green-600" />
                      </div>
                      <p className="text-sm text-gray-600">Ödemeniz başarıyla tamamlandı.</p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                        <XCircle size={32} className="text-red-500" />
                      </div>
                      <p className="text-sm text-gray-600">Ödeme başarısız oldu. Lütfen tekrar deneyin.</p>
                    </>
                  )}
                  <Button
                    onClick={closePaymentModal}
                    className="w-full rounded-full bg-[#6b6de3] hover:bg-[#5658c4]"
                  >
                    Kapat
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Purchase History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Satın Alma Geçmişi</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 overflow-y-auto flex-1">
            {history.length > 0 ? (
              history.map((order) => (
                <div key={order.id} className="p-3 bg-[#F2F2F7] rounded-xl" data-testid={`order-${order.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {order.type === 'subscription' ? order.plan_name : order.package_name}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${order.status === 'completed' ? 'bg-green-100 text-green-600' : order.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'}`}>
                      {order.status === 'completed' ? 'Tamamlandı' : order.status === 'pending' ? 'Bekliyor' : order.status === 'failed' ? 'Başarısız' : 'İptal'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#8E8E93]">
                      {new Date(order.created_at).toLocaleDateString('tr-TR')}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{formatPrice(order.amount)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <History size={40} className="text-[#C7C7CC] mx-auto mb-3" />
                <p className="text-[#8E8E93] text-sm">Henüz satın alma geçmişiniz yok</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionTab;
